import React, { useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Paper,
  TextField,
  Button,
  Tabs,
  Tab,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import {
  SaveRounded as SaveIcon,
  AddRounded as AddIcon,
  DeleteRounded as DeleteIcon,
  ArrowUpwardRounded as UpIcon,
  ArrowDownwardRounded as DownIcon,
  DragIndicatorRounded as DragIcon,
  OpenInNewRounded as OpenInNewIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import { i18n } from "../../../translate/i18n";
import {
  getNavigation,
  saveNavigation,
} from "../../../services/api/siteContent";

// ───── Design tokens
const SP = { xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32, hero: 48 };
const TT = { min: 44, primary: 48, hero: 56 };
const TYPE = {
  caption:    { fontSize: 12, lineHeight: 1.4, letterSpacing: 0.2 },
  body:       { fontSize: 14, lineHeight: 1.5 },
  bodyStrong: { fontSize: 15, lineHeight: 1.45, fontWeight: 500 },
  subheading: { fontSize: 17, lineHeight: 1.4,  fontWeight: 500 },
  heading:    { fontSize: 20, lineHeight: 1.3,  fontWeight: 600 },
};
const RAD = { pill: 999, sm: 8, md: 12, lg: 16, xl: 20 };
const COL = {
  brandGreen:     "#25d366",
  brandGreenDeep: "#128c7e",
  brandGreenSoft: "rgba(37,211,102,0.08)",
  textPrimary:    "#0B0D0C",
  textSecondary:  "#52525b",
  borderSoft:     "#eaeaea",
  pageBg:         "#f7faf8",
  cardBg:         "#ffffff",
  destructive:    "#ef4444",
  warning:        "#f59e0b",
};

const LANGUAGES = [
  { code: "he", label: "עברית", dir: "rtl" },
  { code: "en", label: "English", dir: "ltr" },
  { code: "ru", label: "Русский", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "es", label: "Español", dir: "ltr" },
];

const MOBILE_INPUT_PROPS = { style: { fontSize: 16 } };

// Defensive shape coercion. Backend stores per-locale slots like
//   navigation: { he: { items: [...] }, en: {...}, ... }
//   footer:     { he: { columns: [...], copyright, logoUrl }, ... }
// but seed-time and partial saves can leave the slot as a bare array, a
// stringified object, or `null`. Normalize on read so the editor never
// crashes mid-render.
const normalizeNavSlot = (raw) => {
  if (!raw) return { items: [] };
  if (Array.isArray(raw)) return { items: raw };
  if (typeof raw === "object") {
    const items = Array.isArray(raw.items)
      ? raw.items
      : Array.isArray(raw)
      ? raw
      : [];
    return { ...raw, items };
  }
  if (typeof raw === "string") {
    try {
      return normalizeNavSlot(JSON.parse(raw));
    } catch {
      return { items: [] };
    }
  }
  return { items: [] };
};

const normalizeFooterSlot = (raw) => {
  if (!raw) return { columns: [], copyright: "", logoUrl: "" };
  if (typeof raw === "string") {
    try { return normalizeFooterSlot(JSON.parse(raw)); } catch { return { columns: [], copyright: "", logoUrl: "" }; }
  }
  const columns = Array.isArray(raw.columns)
    ? raw.columns.map((col) => ({
        title: typeof col?.title === "string" ? col.title : "",
        links: Array.isArray(col?.links)
          ? col.links.map((l) => ({
              label: typeof l?.label === "string" ? l.label : "",
              href: typeof l?.href === "string" ? l.href : "",
            }))
          : [],
      }))
    : [];
  return {
    columns,
    copyright: typeof raw.copyright === "string" ? raw.copyright : "",
    logoUrl: typeof raw.logoUrl === "string" ? raw.logoUrl : "",
  };
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  // ───── CRM v3 color vars (dark-mode aware). Mirrors Tags/Campaigns/Contacts.
  const accent     = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair       = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg         = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted    = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  const surfaceBg  = isDark ? "#13181a" : "#ffffff";
  const surfaceAlt = isDark ? "rgba(255,255,255,0.02)" : "#fafbfa";

  return {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: SP.md,
    fontFamily: "'Roboto',system-ui,-apple-system,sans-serif",
    color: fg,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  // ───── Main surface — CRM Paper treatment: 18px radius, hairline border,
  // subtle two-layer shadow. Identical to mainPaper in Tags/Campaigns.
  card: {
    background: surfaceBg,
    border: `1px solid ${hair}`,
    borderRadius: 18,
    padding: SP.xl,
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    boxShadow: isDark
      ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 14px 36px rgba(0,0,0,0.32)"
      : "0 1px 0 rgba(15,22,20,0.02), 0 8px 24px rgba(15,22,20,0.04)",
    [theme.breakpoints.down("xs")]: { padding: SP.md, borderRadius: 14 },
  },
  sectionTitle: {
    ...TYPE.subheading,
    margin: 0,
    marginBottom: SP.xxs,
    color: fg,
    letterSpacing: "-0.005em",
    fontWeight: 600,
    [theme.breakpoints.down("xs")]: {
      fontSize: 16,
    },
  },
  // ───── Accent meta — CRM canonical 10.5px / 0.22em uppercase / accent.
  // Used as the descriptive subtitle under each section title.
  accentMeta: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: accent,
    margin: 0,
    marginBottom: SP.md,
    [theme.breakpoints.down("xs")]: {
      fontSize: 10,
      marginBottom: SP.sm,
    },
  },
  sectionSub: {
    ...TYPE.caption,
    color: fgMuted,
    marginTop: -SP.xxs,
    marginBottom: SP.md,
    [theme.breakpoints.down("xs")]: {
      fontSize: 11,
    },
  },
  // ───── Hairline separator between hero + sections.
  hairline: {
    height: 1,
    background: hair,
    border: "none",
    margin: `${SP.lg}px 0`,
  },
  rowList: {
    display: "flex",
    flexDirection: "column",
    gap: SP.sm,
  },
  // ───── Nav-item card (cardStackMode v3).
  //
  // Layout: drag-handle | input cluster | icon cluster
  // Pseudo `::before` paints the locale-flag color stripe (8px wide,
  // brandGreenDeep by default, overridable via --stripe css var).
  // Subtle hover lift + accent wash.
  navRow: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "auto 1fr 1.5fr 80px auto",
    gap: SP.sm,
    alignItems: "center",
    paddingBlock: SP.md,
    paddingInlineStart: SP.xl, // leaves room for the stripe + breathing
    paddingInlineEnd: SP.sm,
    background: surfaceBg,
    border: `1px solid ${hair}`,
    borderRadius: RAD.lg, // 16 — matches mobileTagCard
    minWidth: 0,
    boxShadow: isDark
      ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 4px 12px rgba(0,0,0,0.18)"
      : "0 1px 0 rgba(15,22,20,0.02), 0 4px 12px rgba(15,22,20,0.03)",
    transition:
      "transform 0.18s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.18s ease, border-color 0.18s ease",
    "& > *": {
      minWidth: 0,
      maxWidth: "100%",
      boxSizing: "border-box",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      insetInlineStart: 0,
      top: 0,
      bottom: 0,
      width: 4,
      background: "var(--stripe, #128c7e)",
      borderStartStartRadius: RAD.lg,
      borderEndStartRadius: RAD.lg,
    },
    "&:hover": {
      borderColor: accentSoft,
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 20px rgba(0,0,0,0.28)"
        : "0 1px 0 rgba(15,22,20,0.02), 0 8px 20px rgba(15,22,20,0.06)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "auto 1fr",
      rowGap: SP.sm,
    },
    // ───── cardStackMode v3 on xs: pure card stack.
    // Stripe stays. Drag-handle floats top-right. Inputs stack at full
    // width. Icon cluster gets a hairline top-border separator.
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: 10,
      paddingInlineStart: 18,
      paddingInlineEnd: SP.sm,
      paddingBlock: SP.md,
      borderRadius: RAD.lg,
      "& > *": {
        width: "100%",
      },
      "& .MuiInputBase-input": {
        fontSize: 16,
      },
    },
  },
  // ───── Drag handle column. Vertical-dots icon, fgMuted, hover → accent.
  // Cursor stays as 'grab' to telegraph the intent even though wiring is
  // arrow-button based today.
  dragHandle: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: fgMuted,
    width: 24,
    height: 36,
    cursor: "grab",
    transition: "color 0.14s ease",
    "&:hover": { color: accent },
    [theme.breakpoints.down("xs")]: {
      position: "absolute",
      top: 8,
      insetInlineEnd: 8,
      width: 28,
      height: 28,
    },
  },
  // ───── Order number chip — small rounded pill on the right of the card.
  // Mirrors the "target chip" treatment in the brief; reused for the order
  // number until live drag-reorder lands.
  targetChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 36,
    height: 24,
    paddingInline: SP.xs,
    background: accentSoft,
    color: accent,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    borderRadius: RAD.pill,
    textTransform: "uppercase",
    fontFeatureSettings: '"tnum" 1',
  },
  iconCluster: {
    display: "inline-flex",
    gap: SP.xs,
    justifyContent: "flex-end",
    [theme.breakpoints.down("xs")]: {
      justifyContent: "flex-end",
      borderTop: `1px solid ${hair}`,
      paddingTop: SP.xs,
      marginTop: SP.xxs,
    },
  },
  iconBtn: {
    minHeight: TT.min,
    minWidth: TT.min,
    padding: 10,
    color: fgMuted,
    transition: "color 0.14s ease, background 0.14s ease",
    "&:hover": {
      color: accent,
      background: accentSoft,
    },
    // Variant: destructive — used for delete buttons via data-variant prop.
    '&[data-variant="danger"]:hover': {
      color: COL.destructive,
      background: isDark ? "rgba(255,107,107,0.08)" : "rgba(220,38,38,0.06)",
    },
  },
  // ───── Sticky add-new card at bottom of each list. Hairline top border,
  // soft accent wash, full-width primary CTA inside.
  addCard: {
    marginTop: SP.md,
    padding: SP.md,
    borderRadius: RAD.lg,
    background: accentSoft,
    border: `1px dashed ${hair}`,
    display: "flex",
    flexDirection: "column",
    gap: SP.xs,
  },
  // ───── Primary CTA on the add card — WhatsApp green, full-width on xs,
  // ≥48px height (TT.primary).
  primaryCTA: {
    background: COL.brandGreen,
    color: "#fff",
    fontWeight: 700,
    textTransform: "none",
    borderRadius: RAD.pill,
    padding: `${SP.sm}px ${SP.lg}px`,
    minHeight: TT.primary,
    fontSize: 14,
    letterSpacing: 0.1,
    boxShadow: "0 4px 12px rgba(37,211,102,0.22)",
    "&:hover": {
      background: COL.brandGreenDeep,
      boxShadow: "0 6px 16px rgba(18,140,126,0.28)",
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  // ───── Legacy add-btn — kept for footer link/column adders, restyled to
  // match the CRM accent treatment.
  addBtn: {
    background: accentSoft,
    color: accent,
    fontWeight: 700,
    textTransform: "none",
    borderRadius: RAD.pill,
    padding: `${SP.xs}px ${SP.md}px`,
    marginTop: SP.sm,
    minHeight: TT.min,
    border: `1px solid ${hair}`,
    "&:hover": {
      background: COL.brandGreen,
      color: "#fff",
      borderColor: COL.brandGreen,
    },
  },
  saveRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: SP.sm,
    marginTop: SP.lg,
    paddingTop: SP.md,
    borderTop: `1px solid ${hair}`,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column-reverse",
    },
  },
  saveBtn: {
    background: COL.brandGreenDeep,
    color: "#fff",
    fontWeight: 700,
    textTransform: "none",
    borderRadius: RAD.pill,
    padding: `${SP.sm}px ${SP.lg}px`,
    minHeight: TT.primary,
    boxShadow: "0 4px 12px rgba(18,140,126,0.22)",
    "&:hover": {
      background: COL.brandGreen,
      boxShadow: "0 6px 16px rgba(37,211,102,0.28)",
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: SP.xxl,
    color: fgMuted,
  },
  langTab: {
    ...TYPE.bodyStrong,
    textTransform: "none",
    minHeight: TT.min,
    minWidth: 0,
    padding: `${SP.xs}px ${SP.md}px`,
    color: fgMuted,
    transition: "color 0.14s ease",
    "&.Mui-selected": { color: accent, fontWeight: 700 },
    [theme.breakpoints.down("xs")]: {
      ...TYPE.body,
      padding: `${SP.xs}px ${SP.sm}px`,
    },
  },
  tabIndicator: {
    background: accent,
    height: 3,
    borderRadius: 3,
  },
  tabsWrap: {
    borderBottom: `1px solid ${hair}`,
    marginBottom: SP.lg,
  },
  // ───── Footer column card. Same surface treatment as navRow but no stripe;
  // tagged as a "container of links" rather than an addressable nav item.
  footerColCard: {
    position: "relative",
    background: surfaceAlt,
    border: `1px solid ${hair}`,
    borderRadius: RAD.lg,
    padding: SP.md,
    display: "flex",
    flexDirection: "column",
    gap: SP.sm,
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  // ───── Footer link row. Subtle alt-surface inside the column card so links
  // visually nest under their column title.
  linkRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr auto",
    gap: SP.xs,
    alignItems: "center",
    padding: SP.xs,
    background: surfaceBg,
    border: `1px solid ${hair}`,
    borderRadius: RAD.md,
    minWidth: 0,
    "& > *": {
      minWidth: 0,
      maxWidth: "100%",
      boxSizing: "border-box",
    },
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: 8,
      "& > *": {
        width: "100%",
      },
      "& .MuiInputBase-input": {
        fontSize: 16,
      },
    },
  },
  footerGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: SP.md,
    minWidth: 0,
    marginBottom: SP.md,
    "& > *": {
      minWidth: 0,
      maxWidth: "100%",
      boxSizing: "border-box",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
    [theme.breakpoints.down("xs")]: {
      "& .MuiInputBase-input": {
        fontSize: 16,
      },
    },
  },
  colTitleRow: {
    display: "flex",
    gap: SP.sm,
    alignItems: "center",
    paddingBottom: SP.xs,
    borderBottom: `1px solid ${hair}`,
    [theme.breakpoints.down("xs")]: {
      "& .MuiInputBase-input": {
        fontSize: 16,
      },
    },
  },
  divider: {
    margin: `${SP.lg}px 0`,
    background: hair,
  },
  // ───── Entrance animation for nav cards (matches CRM tagCardIn).
  "@keyframes navCardIn": {
    "0%":   { opacity: 0, transform: "translateY(8px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  navRowAnim: {
    opacity: 0,
    animation: "$navCardIn 0.42s cubic-bezier(0.16, 1, 0.3, 1) forwards",
    animationDelay: "calc(var(--i, 0) * 40ms)",
  },
  };
});

// Reorder helper that doesn't depend on a drag library — array-swap with the
// previous/next element. Bounds-checked so the up button on index 0 and down
// button on the last index are no-ops.
const swap = (arr, idx, dir) => {
  const target = idx + dir;
  if (target < 0 || target >= arr.length) return arr;
  const out = arr.slice();
  [out[idx], out[target]] = [out[target], out[idx]];
  return out;
};

const NavigationEditor = () => {
  const classes = useStyles();
  const [langIdx, setLangIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Full per-locale state. `navigation[lang].items` is an array of nav items;
  // `footer[lang]` is { columns, copyright, logoUrl }.
  const [navigation, setNavigation] = useState({});
  const [footer, setFooter] = useState({});

  const currentLang = LANGUAGES[langIdx]?.code || "he";
  const navSlot = navigation[currentLang] || { items: [] };
  const footerSlot = footer[currentLang] || { columns: [], copyright: "", logoUrl: "" };

  // Fetch full per-locale blob on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getNavigation();
        if (cancelled) return;
        const navAll = {};
        const footerAll = {};
        for (const l of LANGUAGES) {
          navAll[l.code] = normalizeNavSlot(data?.navigation?.[l.code]);
          footerAll[l.code] = normalizeFooterSlot(data?.footer?.[l.code]);
        }
        setNavigation(navAll);
        setFooter(footerAll);
      } catch (err) {
        toast.error(
          i18n.t("siteContent.errors.loadNav", {
            defaultValue: "Failed to load navigation",
          })
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Nav item handlers ──────────────────────────────────────────────────────
  const patchNav = useCallback((patch) => {
    setNavigation((prev) => ({
      ...prev,
      [currentLang]: { ...(prev[currentLang] || { items: [] }), ...patch },
    }));
  }, [currentLang]);

  const updateNavItem = (idx, field, value) => {
    const next = (navSlot.items || []).slice();
    next[idx] = { ...next[idx], [field]: value };
    patchNav({ items: next });
  };

  const removeNavItem = (idx) => {
    const next = (navSlot.items || []).slice();
    next.splice(idx, 1);
    patchNav({ items: next });
  };

  const moveNavItem = (idx, dir) => {
    patchNav({ items: swap(navSlot.items || [], idx, dir) });
  };

  const addNavItem = () => {
    const next = (navSlot.items || []).slice();
    next.push({ label: "", href: "", order: next.length });
    patchNav({ items: next });
  };

  // ── Footer handlers ────────────────────────────────────────────────────────
  const patchFooter = useCallback((patch) => {
    setFooter((prev) => ({
      ...prev,
      [currentLang]: { ...(prev[currentLang] || { columns: [], copyright: "", logoUrl: "" }), ...patch },
    }));
  }, [currentLang]);

  const updateColumnTitle = (colIdx, value) => {
    const cols = (footerSlot.columns || []).slice();
    cols[colIdx] = { ...cols[colIdx], title: value };
    patchFooter({ columns: cols });
  };

  const addColumn = () => {
    const cols = (footerSlot.columns || []).slice();
    cols.push({ title: "", links: [] });
    patchFooter({ columns: cols });
  };

  const removeColumn = (colIdx) => {
    const cols = (footerSlot.columns || []).slice();
    cols.splice(colIdx, 1);
    patchFooter({ columns: cols });
  };

  const addLink = (colIdx) => {
    const cols = (footerSlot.columns || []).slice();
    const links = (cols[colIdx]?.links || []).slice();
    links.push({ label: "", href: "" });
    cols[colIdx] = { ...cols[colIdx], links };
    patchFooter({ columns: cols });
  };

  const updateLink = (colIdx, linkIdx, field, value) => {
    const cols = (footerSlot.columns || []).slice();
    const links = (cols[colIdx]?.links || []).slice();
    links[linkIdx] = { ...links[linkIdx], [field]: value };
    cols[colIdx] = { ...cols[colIdx], links };
    patchFooter({ columns: cols });
  };

  const removeLink = (colIdx, linkIdx) => {
    const cols = (footerSlot.columns || []).slice();
    const links = (cols[colIdx]?.links || []).slice();
    links.splice(linkIdx, 1);
    cols[colIdx] = { ...cols[colIdx], links };
    patchFooter({ columns: cols });
  };

  const handleSave = useCallback(async () => {
    // Normalize order to match array index just before send — operator may
    // have reordered without touching the `order` field.
    const orderedItems = (navSlot.items || []).map((it, idx) => ({
      label: it?.label || "",
      href: it?.href || "",
      order: Number.isFinite(Number(it?.order)) ? Number(it.order) : idx,
    }));
    try {
      setSaving(true);
      await saveNavigation({
        locale: currentLang,
        navigation: { ...navSlot, items: orderedItems },
        footer: footerSlot,
      });
      toast.success(
        i18n.t("siteContent.toasts.navSaved", {
          defaultValue: "Navigation saved",
        })
      );
    } catch (err) {
      toast.error(
        i18n.t("siteContent.errors.saveNav", {
          defaultValue: "Failed to save navigation",
        })
      );
    } finally {
      setSaving(false);
    }
  }, [currentLang, navSlot, footerSlot]);

  if (loading) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress size={28} style={{ color: COL.brandGreenDeep }} />
      </div>
    );
  }

  const dir = LANGUAGES[langIdx].dir;

  return (
    <div className={classes.root}>
      <Paper elevation={0} className={classes.card}>
        <div className={classes.tabsWrap}>
          <Tabs
            value={langIdx}
            onChange={(_e, v) => setLangIdx(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            classes={{ indicator: classes.tabIndicator }}
            aria-label={i18n.t("siteContent.langTabsLabel", {
              defaultValue: "Language",
            })}
          >
            {LANGUAGES.map((l) => (
              <Tab key={l.code} className={classes.langTab} label={l.label} />
            ))}
          </Tabs>
        </div>

        {/* ── Top navigation ─────────────────────────────────────────────── */}
        <h3 className={classes.sectionTitle}>
          {i18n.t("siteContent.nav.topTitle", { defaultValue: "Top navigation" })}
        </h3>
        <p className={classes.accentMeta}>
          {i18n.t("siteContent.nav.topSubtitle", {
            defaultValue: "Header links shown on whapiz.com",
          })}
        </p>

        <div className={classes.rowList}>
          {(navSlot.items || []).map((item, idx) => {
            const hrefValue = (item.href || "").trim();
            const isLast = idx === (navSlot.items || []).length - 1;
            return (
              <div
                key={idx}
                className={`${classes.navRow} ${classes.navRowAnim}`}
                style={{ "--i": idx, "--stripe": COL.brandGreenDeep }}
              >
                <span
                  className={classes.dragHandle}
                  aria-hidden="true"
                  title={i18n.t("siteContent.nav.drag", { defaultValue: "Drag to reorder" })}
                >
                  <DragIcon fontSize="small" />
                </span>
                <TextField
                  label={i18n.t("siteContent.nav.label", { defaultValue: "Label" })}
                  value={item.label || ""}
                  onChange={(e) => updateNavItem(idx, "label", e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={MOBILE_INPUT_PROPS}
                  dir={dir}
                />
                <TextField
                  label={i18n.t("siteContent.nav.href", { defaultValue: "Href" })}
                  value={item.href || ""}
                  onChange={(e) => updateNavItem(idx, "href", e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={MOBILE_INPUT_PROPS}
                  placeholder="/pricing"
                />
                <TextField
                  label={i18n.t("siteContent.nav.order", { defaultValue: "Order" })}
                  value={Number.isFinite(Number(item.order)) ? item.order : idx}
                  onChange={(e) => updateNavItem(idx, "order", Number(e.target.value))}
                  variant="outlined"
                  size="small"
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ ...MOBILE_INPUT_PROPS, min: 0 }}
                />
                <div className={classes.iconCluster}>
                  {hrefValue && (
                    <IconButton
                      className={classes.iconBtn}
                      component="a"
                      href={hrefValue}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open href in new tab"
                      size="small"
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  )}
                  <IconButton
                    className={classes.iconBtn}
                    onClick={() => moveNavItem(idx, -1)}
                    disabled={idx === 0}
                    aria-label="Move up"
                    size="small"
                  >
                    <UpIcon />
                  </IconButton>
                  <IconButton
                    className={classes.iconBtn}
                    onClick={() => moveNavItem(idx, 1)}
                    disabled={isLast}
                    aria-label="Move down"
                    size="small"
                  >
                    <DownIcon />
                  </IconButton>
                  <IconButton
                    className={classes.iconBtn}
                    data-variant="danger"
                    onClick={() => removeNavItem(idx)}
                    aria-label="Delete"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Sticky add-new card. CRM accent wash + primary green CTA. */}
        <div className={classes.addCard}>
          <Button
            className={classes.primaryCTA}
            startIcon={<AddIcon />}
            onClick={addNavItem}
            fullWidth
          >
            {i18n.t("siteContent.nav.add", { defaultValue: "Add navigation item" })}
          </Button>
        </div>

        <hr className={classes.hairline} />

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <h3 className={classes.sectionTitle}>
          {i18n.t("siteContent.footer.title", { defaultValue: "Footer" })}
        </h3>
        <p className={classes.accentMeta}>
          {i18n.t("siteContent.footer.subtitle", {
            defaultValue: "Columns · copyright · logo URL",
          })}
        </p>

        <Box className={classes.footerGrid}>
          <TextField
            label={i18n.t("siteContent.footer.logoUrl", {
              defaultValue: "Logo URL",
            })}
            value={footerSlot.logoUrl || ""}
            onChange={(e) => patchFooter({ logoUrl: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
            placeholder="https://whapiz.com/static/logo.svg"
          />
          <TextField
            label={i18n.t("siteContent.footer.copyright", {
              defaultValue: "Copyright text",
            })}
            value={footerSlot.copyright || ""}
            onChange={(e) => patchFooter({ copyright: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
            dir={dir}
          />
        </Box>

        <div className={classes.rowList} style={{ marginTop: SP.md }}>
          {(footerSlot.columns || []).map((col, colIdx) => (
            <div key={colIdx} className={classes.footerColCard}>
              <div className={classes.colTitleRow}>
                <TextField
                  label={i18n.t("siteContent.footer.colTitle", {
                    defaultValue: "Column title",
                  })}
                  value={col.title || ""}
                  onChange={(e) => updateColumnTitle(colIdx, e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={MOBILE_INPUT_PROPS}
                  dir={dir}
                />
                <IconButton
                  className={classes.iconBtn}
                  onClick={() => removeColumn(colIdx)}
                  aria-label="Delete column"
                  size="small"
                  style={{ color: COL.destructive }}
                >
                  <DeleteIcon />
                </IconButton>
              </div>

              {(col.links || []).map((link, linkIdx) => (
                <div key={linkIdx} className={classes.linkRow}>
                  <TextField
                    label={i18n.t("siteContent.footer.linkLabel", {
                      defaultValue: "Link label",
                    })}
                    value={link.label || ""}
                    onChange={(e) =>
                      updateLink(colIdx, linkIdx, "label", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={MOBILE_INPUT_PROPS}
                    dir={dir}
                  />
                  <TextField
                    label={i18n.t("siteContent.footer.linkHref", {
                      defaultValue: "Link href",
                    })}
                    value={link.href || ""}
                    onChange={(e) =>
                      updateLink(colIdx, linkIdx, "href", e.target.value)
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={MOBILE_INPUT_PROPS}
                  />
                  <IconButton
                    className={classes.iconBtn}
                    onClick={() => removeLink(colIdx, linkIdx)}
                    aria-label="Delete link"
                    size="small"
                    style={{ color: COL.destructive }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              ))}

              <Button
                className={classes.addBtn}
                startIcon={<AddIcon />}
                onClick={() => addLink(colIdx)}
                style={{ alignSelf: "flex-start", marginTop: 0 }}
              >
                {i18n.t("siteContent.footer.addLink", {
                  defaultValue: "Add link",
                })}
              </Button>
            </div>
          ))}
        </div>

        <Button
          className={classes.addBtn}
          startIcon={<AddIcon />}
          onClick={addColumn}
        >
          {i18n.t("siteContent.footer.addCol", {
            defaultValue: "Add footer column",
          })}
        </Button>

        <div className={classes.saveRow}>
          <Button
            onClick={handleSave}
            className={classes.saveBtn}
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={16} style={{ color: "#fff" }} />
              ) : (
                <SaveIcon />
              )
            }
          >
            {saving
              ? i18n.t("siteContent.saving", { defaultValue: "Saving…" })
              : i18n.t("siteContent.saveNav", {
                  defaultValue: "Save navigation",
                })}
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default NavigationEditor;
